import {
  $applyNodeReplacement,
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
} from 'lexical';
import { ReactNode } from 'react';

import { CitationPopper } from './CitationPopper';

export const CITATION_NODE_DATA_ATTRIBUTE =
  'data-lexical-citation-paragraph-ids';

export function serializeCitationParagraphIds(paragraphIds: string[]): string {
  return JSON.stringify(paragraphIds);
}

export function parseCitationParagraphIds(value: string): string[] | null {
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed.every((id): id is string => typeof id === 'string')
    ) {
      return parsed;
    }
  } catch {
    // Invalid clipboard metadata is ignored so normal paste handling can run.
  }
  return null;
}

export function containsSerializedCitationNode(html: string): boolean {
  if (!html) return false;

  const document = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(
    document.querySelectorAll(`[${CITATION_NODE_DATA_ATTRIBUTE}]`)
  ).some((element) => {
    const value = element.getAttribute(CITATION_NODE_DATA_ATTRIBUTE);
    return value !== null && parseCitationParagraphIds(value) !== null;
  });
}

/**
 * A node that represents a citation in the text editor.
 * A raw citation is a string that contains an array of objected containing citation text, section identifier, and section title. It looks like this:
 * [citations=["fileparagraph_123", "fileparagraph_456"]]
 */

export class CitationNode extends DecoratorNode<ReactNode> {
  __paragraphIds: string[];

  static override getType(): string {
    return 'citation';
  }

  static override clone(node: CitationNode): CitationNode {
    return $applyNodeReplacement(
      new CitationNode(node.__paragraphIds, node.__key)
    );
  }

  constructor(paragraphIds: string[], __key?: string) {
    super(__key);
    this.__paragraphIds = paragraphIds;
  }

  override createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.setAttribute('data-testid', 'CitationNode');
    return span;
  }

  override updateDOM(): false {
    return false;
  }

  override exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute(
      CITATION_NODE_DATA_ATTRIBUTE,
      serializeCitationParagraphIds(this.__paragraphIds)
    );
    return { element };
  }

  static override importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute(CITATION_NODE_DATA_ATTRIBUTE)) {
          return null;
        }
        return {
          conversion: $convertCitationElement,
          priority: 2,
        };
      },
    };
  }

  override exportJSON(): {
    paragraphIds: string[];
    type: string;
    version: number;
  } {
    return {
      paragraphIds: this.__paragraphIds,
      type: CitationNode.getType(),
      version: 1,
    };
  }

  static override importJSON(serializedNode: {
    paragraphIds: string[];
    type: string;
    version: number;
  }): CitationNode {
    const node = $createCitationNode(serializedNode.paragraphIds);
    return node;
  }

  override decorate(): ReactNode {
    return (
      <CitationPopper key={this.__key} paragraphIds={this.__paragraphIds} />
    );
  }

  getParagraphIds(): string[] {
    return this.__paragraphIds;
  }
}

export function $createCitationNode(paragraphIds: string[]): CitationNode {
  return new CitationNode(paragraphIds);
}

function $convertCitationElement(
  element: HTMLElement
): DOMConversionOutput | null {
  const serializedParagraphIds = element.getAttribute(
    CITATION_NODE_DATA_ATTRIBUTE
  );
  if (serializedParagraphIds === null) return null;

  const paragraphIds = parseCitationParagraphIds(serializedParagraphIds);
  return paragraphIds ? { node: $createCitationNode(paragraphIds) } : null;
}
